import { useState } from "react";
import { doc, setDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../../../firebase";

/**
 * EditProfileForm - Inline edit form for profile owner
 * Allows editing bio and location with upsert to social_profile/{uid}
 */
const EditProfileForm = ({ uid, initialBio = "", initialLocation = "", onSave }) => {
    const [isEditing, setIsEditing] = useState(false);
    const [bio, setBio] = useState(initialBio);
    const [location, setLocation] = useState(initialLocation);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState("");

    async function handleSave() {
        if (isSaving) return;
        setIsSaving(true);
        setError("");

        try {
            await setDoc(
                doc(db, "social_profile", uid),
                {
                    bio: bio.trim(),
                    location: location.trim(),
                    updatedAt: serverTimestamp(),
                },
                { merge: true }
            );

            // Notify parent of save
            onSave?.({
                bio: bio.trim(),
                location: location.trim(),
            });

            setIsEditing(false);
        } catch (err) {
            console.error("Error saving profile:", err.message);
            setError("Failed to save profile. Please try again.");
        }

        setIsSaving(false);
    }

    function handleCancel() {
        setBio(initialBio);
        setLocation(initialLocation);
        setIsEditing(false);
        setError("");
    }

    if (!isEditing) {
        return (
            <div className="mt-4 pt-4 border-t border-slate-100">
                <button
                    onClick={() => setIsEditing(true)}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors"
                >
                    Edit Profile
                </button>
            </div>
        );
    }

    return (
        <div className="mt-4 pt-4 border-t border-slate-100">
            <h3 className="text-sm font-semibold text-slate-700 mb-3">Edit Profile</h3>

            {/* Location Input */}
            <div className="mb-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                    Location
                </label>
                <input
                    type="text"
                    value={location}
                    onChange={(e) => setLocation(e.target.value)}
                    placeholder="e.g. Toronto, Canada"
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    maxLength={100}
                />
            </div>

            {/* Bio Textarea */}
            <div className="mb-3">
                <label className="block text-xs font-medium text-slate-500 mb-1">
                    Bio
                </label>
                <textarea
                    value={bio}
                    onChange={(e) => setBio(e.target.value)}
                    placeholder="Tell us about yourself..."
                    rows={3}
                    className="w-full px-3 py-2 border border-slate-200 rounded-lg text-sm resize-none focus:outline-none focus:border-blue-500 focus:ring-1 focus:ring-blue-500"
                    maxLength={500}
                />
                <p className="text-xs text-slate-400 mt-1">{bio.length}/500</p>
            </div>

            {/* Error Message */}
            {error && (
                <p className="text-red-500 text-sm mb-3">{error}</p>
            )}

            {/* Action Buttons */}
            <div className="flex gap-2">
                <button
                    onClick={handleSave}
                    disabled={isSaving}
                    className="px-4 py-2 bg-blue-500 hover:bg-blue-600 text-white rounded-lg text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                    {isSaving ? "Saving..." : "Save"}
                </button>
                <button
                    onClick={handleCancel}
                    disabled={isSaving}
                    className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                    Cancel
                </button>
            </div>
        </div>
    );
};

export default EditProfileForm;
